export interface PlanLimitErrorDetail {
  code: 'plan_limit_reached';
  plan: string;
  resource: string;
  limit: number;
  message: string;
}

export interface ParsedApiError {
  /** Always a plain string: safe to pass to toast.error or render as a child. */
  message: string;
  planLimit: PlanLimitErrorDetail | null;
  /** Field name (last `loc` segment of a 422) → message, for forms that show them inline. */
  fieldErrors: Record<string, string>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isPlanLimitDetail(value: unknown): value is PlanLimitErrorDetail {
  return (
    isRecord(value) &&
    value.code === 'plan_limit_reached' &&
    typeof value.plan === 'string' &&
    typeof value.resource === 'string' &&
    typeof value.limit === 'number' &&
    typeof value.message === 'string'
  );
}

// Where FastAPI puts the request part a validation error is about; they are
// not field names.
const LOCATION_ROOTS = new Set(['body', 'query', 'path', 'header', 'cookie']);

function fieldName(loc: unknown): string | null {
  if (!Array.isArray(loc)) return null;
  const names = loc.filter((part): part is string => typeof part === 'string' && !LOCATION_ROOTS.has(part));
  return names.length ? names[names.length - 1] : null;
}

function fieldLabel(field: string): string {
  const words = field.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export interface ParseApiErrorOptions {
  /**
   * The prefix naming the field a validation message is about, from its full
   * `loc`; `null` for no prefix. Defaults to the humanized field name
   * ("new_password" → "New password").
   */
  label?: (loc: unknown[], field: string | null) => string | null;
}

const defaultLabel = (_loc: unknown[], field: string | null) => (field ? fieldLabel(field) : null);

/**
 * The email-validator library explains *why* an address is invalid in terms only a
 * developer follows ("The part after the @-sign is a special-use or reserved name…").
 * Users need to know what to type instead.
 */
function friendlyValidationMessage(msg: string): string {
  if (/^value is not a valid email address\b/i.test(msg)) {
    return 'Enter a valid e-mail address, like name@company.com.';
  }
  return msg;
}

/**
 * FastAPI's 422 `detail` is an array of `{type, loc, msg, input, ctx}`. Rendering
 * it as-is crashed the login page (React error #31, FRONTEND-WEB-USER-14).
 */
function parseValidationErrors(
  detail: unknown[],
  label: NonNullable<ParseApiErrorOptions['label']>,
): Pick<ParsedApiError, 'message' | 'fieldErrors'> {
  const messages: string[] = [];
  const fieldErrors: Record<string, string> = {};
  for (const item of detail) {
    if (typeof item === 'string' && item.trim()) {
      messages.push(item.trim());
      continue;
    }
    if (!isRecord(item) || typeof item.msg !== 'string' || !item.msg.trim()) continue;
    const msg = friendlyValidationMessage(item.msg.trim().replace(/^Value error, /, ''));
    const field = fieldName(item.loc);
    if (field) fieldErrors[field] ??= msg;
    const prefix = label(Array.isArray(item.loc) ? item.loc : [], field);
    messages.push(prefix ? `${prefix}: ${msg}` : msg);
  }
  return { message: [...new Set(messages)].join(' '), fieldErrors };
}

/**
 * The one place API error payloads become UI copy. Handles FastAPI's string
 * details, its 422 validation arrays, structured `{message}` details (plan
 * limits), and the public API's `{error: {code, message}}` envelope. Axios
 * types alone cannot describe every endpoint's error envelope, so keep the
 * runtime checks here instead of scattering casts: never read
 * `error.response.data.detail` anywhere else (lib/api/errors.guard.test.ts).
 */
export function parseApiError(
  error: unknown,
  fallback: string,
  options: ParseApiErrorOptions = {},
): ParsedApiError {
  const response = isRecord(error) && isRecord(error.response) ? error.response : null;
  const data = response && isRecord(response.data) ? response.data : null;
  const detail = data?.detail ?? data?.error;

  if (typeof detail === 'string' && detail.trim()) {
    return { message: detail, planLimit: null, fieldErrors: {} };
  }

  if (Array.isArray(detail)) {
    const parsed = parseValidationErrors(detail, options.label ?? defaultLabel);
    if (parsed.message) {
      return { ...parsed, planLimit: null };
    }
  }

  if (isPlanLimitDetail(detail)) {
    return { message: detail.message, planLimit: detail, fieldErrors: {} };
  }

  if (isRecord(detail) && typeof detail.message === 'string' && detail.message.trim()) {
    return { message: detail.message, planLimit: null, fieldErrors: {} };
  }

  // Axios' own message ("Request failed with status code 500") says less than
  // the caller's copy; a plain Error's message is still worth showing.
  const isAxiosError = isRecord(error) && error.isAxiosError === true;
  if (error instanceof Error && !isAxiosError && error.message.trim()) {
    return { message: error.message, planLimit: null, fieldErrors: {} };
  }

  return { message: fallback, planLimit: null, fieldErrors: {} };
}

export function apiErrorMessage(
  error: unknown,
  fallback: string,
  options?: ParseApiErrorOptions,
): string {
  return parseApiError(error, fallback, options).message;
}

/**
 * Put a 422's per-field messages on a react-hook-form form. Only `fields` the
 * form actually has are set. Returns true when at least one was set, so the
 * caller can skip a redundant toast.
 */
export function setServerFieldErrors<T extends string>(
  error: unknown,
  fields: readonly T[],
  setError: (name: T, error: { type: string; message: string }) => void,
): boolean {
  const { fieldErrors } = parseApiError(error, '');
  let set = false;
  for (const field of fields) {
    const message = fieldErrors[field];
    if (message) {
      setError(field, { type: 'server', message });
      set = true;
    }
  }
  return set;
}

export interface ApiValidationIssue {
  code: string;
  message: string;
  node_id?: string;
  edge_id?: string;
}

/**
 * The coded issue list of a workflow save's 422 (`{detail: {errors: [...]}}`),
 * or an empty list when the error is not one.
 */
export function workflowValidationErrors(error: unknown): ApiValidationIssue[] {
  const response = isRecord(error) && isRecord(error.response) ? error.response : null;
  const data = response && isRecord(response.data) ? response.data : null;
  const detail = data?.detail;
  if (!isRecord(detail) || !Array.isArray(detail.errors)) return [];
  return detail.errors.filter(
    (e): e is ApiValidationIssue =>
      isRecord(e) && typeof e.code === 'string' && typeof e.message === 'string',
  );
}
